import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Utensils, Music, Heart, Calendar } from "lucide-react";
import { ASSETS, api, CATEGORY_LABELS } from "@/lib/api";
import { RESTAURANT } from "@/lib/restaurant";
import GoogleReviewsSection from "@/components/GoogleReviews";
import SEO from "@/components/SEO";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/menu").then((r) => {
      setFeatured(r.data.filter((m) => m.featured).slice(0, 6));
    }).catch(() => {});
    api.get("/events").then((r) => {
      setEvents(r.data.slice(0, 3));
    }).catch(() => {});
    api.get("/site/content").then((r) => setContent(r.data || {})).catch(() => {});
  }, []);
  const c = content;

  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description="Restaurant portugais familial à Trappes (78). Plats du jour savoureux, cuisine traditionnelle (bacalhau, polvo, pastéis de nata) et soirées dansantes le week-end. Réservation en ligne et privatisation salle dès 12 personnes."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.heroInterior})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/75" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 pb-16 md:pb-24 pt-32 w-full grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow !text-[#FFD7A6] animate-fade-up">
              {c.hero_eyebrow || "Restaurant portugais · Trappes"}
            </div>
            <h1 className="font-serif-display text-white text-5xl md:text-7xl lg:text-[88px] leading-[0.95] mt-4 animate-fade-up delay-1">
              {c.hero_title_line1 || "Saveurs du Portugal,"}<br />
              {c.hero_title_line2 || "chaleur de la famille."}
            </h1>
            <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl animate-fade-up delay-2">
              {c.hero_description || "Plats du jour cuisinés avec passion en semaine, fêtes conviviales le week-end. Au Globo Dancing, tout le monde se sent comme à la maison — même quand on chante et qu'on danse."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 animate-fade-up delay-3">
              <Link to="/reserver" data-testid="hero-reserve-btn" className="btn-primary">
                Réserver une table <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link to="/menu" data-testid="hero-menu-btn" className="btn-secondary !bg-white/10 !border-white/40 !text-white hover:!bg-white hover:!text-[#1F476B]">
                Voir la carte
              </Link>
            </div>
          </div>

          {/* Hero side info card */}
          <aside className="hidden lg:block lg:col-span-4 animate-fade-up delay-4">
            <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-7 text-white">
              <div className="eyebrow !text-[#FFD7A6] mb-4">Aujourd'hui au Globo</div>
              <ul className="space-y-4 text-[15px]">
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFB59A] mt-2 shrink-0" />
                  <div>
                    <div className="font-serif-display text-lg leading-tight">Plat du jour</div>
                    <div className="text-white/70">{c.hero_today_dish || "Cozido à Portuguesa — 19,00 €"}</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#9CC5E0] mt-2 shrink-0" />
                  <div>
                    <div className="font-serif-display text-lg leading-tight">Soirée du week-end</div>
                    <div className="text-white/70">{c.hero_today_soiree || "Vendredi · Fado live, 20h"}</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFD7A6] mt-2 shrink-0" />
                  <div>
                    <div className="font-serif-display text-lg leading-tight">Ouverture</div>
                    <div className="text-white/70">{c.hero_today_hours || "12h–14h30 · 19h–22h30"}</div>
                  </div>
                </li>
              </ul>
              <Link
                to="/reserver"
                className="mt-6 inline-flex items-center gap-2 text-[#FFD7A6] hover:text-white transition text-sm font-medium"
              >
                Réserver pour ce soir <ArrowRight size={14} />
              </Link>
            </div>
          </aside>
        </div>
        {/* Marquee strip */}
        <div className="absolute bottom-0 inset-x-0 bg-[#C04E38] text-white py-3 overflow-hidden">
          <div className="flex marquee-track gap-12 whitespace-nowrap font-serif-display italic text-lg">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-12">
                <span>★ Bacalhau à Brás</span>
                <span>★ Pastéis de Nata</span>
                <span>★ Soirées Fado le vendredi</span>
                <span>★ Vinho Verde frais</span>
                <span>★ Polvo à Lagareiro</span>
                <span>★ Saudade & convivialité</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO BENTO */}
      <section className="py-24 md:py-32 azulejo-bg grain">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5 reveal">
            <div className="eyebrow">{c.about_eyebrow || "A casa do Globo"}</div>
            <h2 className="text-4xl md:text-5xl mt-4 leading-tight">
              {c.about_title || "Une table portugaise vivante au cœur de Trappes"}
            </h2>
            <p className="text-[#5C5A56] mt-6 text-lg leading-relaxed whitespace-pre-line">
              {c.about_text || "Chez nous, chaque plat raconte une histoire : la mer du Nord, les vignes du Douro, le sourire des grands-mères qui mijotent depuis toujours. La semaine on déguste ; le week-end, on chante, on danse, on partage."}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <span className="px-4 py-2 rounded-full bg-white border border-[#E6E1D6] text-sm">Cuisine maison</span>
              <span className="px-4 py-2 rounded-full bg-white border border-[#E6E1D6] text-sm">Familial</span>
              <span className="px-4 py-2 rounded-full bg-white border border-[#E6E1D6] text-sm">Soirées Fado</span>
              <span className="px-4 py-2 rounded-full bg-white border border-[#E6E1D6] text-sm">Vins du Douro</span>
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 h-[480px] md:h-[560px] reveal">
            <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden">
              <img src={ASSETS.diningRoom} alt="La salle du Globo Dancing" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700" />
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden">
              <img src={ASSETS.tableDetail} alt="Dressage de table" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700" />
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden">
              <img src={ASSETS.pasteis} alt="Pastéis de Nata" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700" />
            </div>
            <div className="col-span-4 row-span-2 rounded-2xl overflow-hidden">
              <img src={ASSETS.bar} alt="Espace bar et soirées" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700" />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-8">
          {[
            { icon: Utensils, title: "Plats du jour", text: "Une carte qui change selon l'humeur du chef et les arrivages du marché." },
            { icon: Music, title: "Soirées Fado", text: "Tous les vendredis et samedis, vibrez avec les chants traditionnels portugais." },
            { icon: Heart, title: "Famille avant tout", text: "Anniversaires, baptêmes, dimanches en tribu — on vous reçoit comme chez vous." },
          ].map((p, i) => (
            <div key={i} className="card-warm p-8 reveal" data-testid={`pillar-${i}`}>
              <div className="w-12 h-12 rounded-full bg-[#C04E38]/10 text-[#C04E38] grid place-items-center mb-5">
                <p.icon size={22} />
              </div>
              <h3 className="text-2xl mb-3">{p.title}</h3>
              <p className="text-[#5C5A56] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS BANNER */}
      <section className="bg-[#1F476B] text-white py-16 md:py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.facade})` }}
        />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 reveal">
          {[
            { n: "20+", l: "Recettes traditionnelles" },
            { n: "7/7", l: "Sauf le lundi" },
            { n: "Fado", l: "Vendredi & samedi" },
            { n: `★ ${RESTAURANT.google.rating.toFixed(1)}`, l: `${RESTAURANT.google.reviewCount} avis Google` },
          ].map((s, i) => (
            <div key={i} className="text-center md:text-left border-l-2 border-[#C04E38] pl-5" data-testid={`stat-${i}`}>
              <div className="font-serif-display text-5xl md:text-6xl text-white">{s.n}</div>
              <div className="text-white/70 text-sm uppercase tracking-widest mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED MENU */}
      {featured.length > 0 && (
        <section className="py-24 bg-[#F4F1EA]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4 reveal">
              <div>
                <div className="eyebrow">Os pratos da casa</div>
                <h2 className="text-4xl md:text-5xl mt-3">Les incontournables</h2>
              </div>
              <Link to="/menu" className="btn-secondary self-start" data-testid="see-full-menu">
                Voir toute la carte <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((m, i) => (
                <article key={m.id} className="card-warm p-7 reveal" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`featured-item-${m.id}`}>
                  <div className="flex justify-between gap-4 mb-3">
                    <h3 className="text-xl">{m.name}</h3>
                    {m.price && <span className="font-serif-display text-[#C04E38] text-lg whitespace-nowrap">{m.price}</span>}
                  </div>
                  <p className="text-sm text-[#5C5A56] leading-relaxed">{m.description}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#1F476B]">
                    <Star size={12} fill="#1F476B" /> {CATEGORY_LABELS[m.category] || m.category}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      <GoogleReviewsSection />

      {/* EVENTS PREVIEW */}
      {events.length > 0 && (
        <section className="py-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4 reveal">
              <div>
                <div className="eyebrow">Próximas festas</div>
                <h2 className="text-4xl md:text-5xl mt-3">Soirées à venir</h2>
              </div>
              <Link to="/evenements" className="btn-secondary self-start" data-testid="see-all-events">
                Tous les événements <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {events.map((e, i) => (
                <article key={e.id} className="card-warm overflow-hidden reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`event-card-${e.id}`}>
                  {e.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={e.image_url} alt={e.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C04E38] mb-2">
                      <Calendar size={12} /> {new Date(e.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} {e.time && `· ${e.time}`}
                    </div>
                    <h3 className="text-xl mb-2">{e.title}</h3>
                    <p className="text-sm text-[#5C5A56] leading-relaxed">{e.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-[#2C2B29] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center reveal">
          <div className="eyebrow !text-[#FFB59A]">Reserve a sua mesa</div>
          <h2 className="text-4xl md:text-6xl mt-4 text-white">{c.cta_title || "Une envie de Portugal ?"}</h2>
          <p className="text-white/75 text-lg mt-5 max-w-2xl mx-auto whitespace-pre-line">
            {c.cta_text || "Réservez votre table en quelques clics. Nous confirmons par email ou téléphone, et préparons votre arrivée comme il se doit."}
          </p>
          <Link to="/reserver" className="btn-primary mt-8" data-testid="cta-reserve-btn">
            Réserver maintenant <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
